import { createClient } from '@elastic/app-search-javascript';
import { debounce } from 'scripts/helpers/_utils';

const PAGE_SIZE = 10;
const DEBOUNCE_TIME = 250;

const highlightTitle = ( title, searchQuery ) => {
	const replaceRegex = new RegExp( searchQuery, 'gi' );

	if ( title && title.match( replaceRegex ) ) {
		// Preserves the original case of the search query.
		title.match( replaceRegex ).forEach( ( exactMatch ) => {
			title = title.replace( exactMatch, `<em>${ exactMatch }</em>` );
		} );
	}

	return title;
};

export const getClient = ( engine ) => {
	return createClient( {
		endpointBase: 'https://webops-production.ent.us-central1.gcp.cloud.es.io/',
		searchKey: 'search-on1avk1ufmwckpa77rjqhpr6',
		engineName: engine,
	} );
};

export const initSearch = () => {
	const client = getClient( 'freshbooks-search-resource-hub' );
	const searchField = document.querySelector( '.st-default-search-input' );

	if ( client && searchField ) {
		handleSearchAutoComplete( client, searchField );
		handleSearchModal( client, searchField );
	}
};

export const attachAnalyticsClickTracking = ( client ) => {
	Array.from( document.querySelectorAll( 'a[data-document-id]' ) ).forEach( ( linkToTrack ) => {
		linkToTrack.addEventListener( 'click', () => {
			client.click( {
				query: linkToTrack.getAttribute( 'data-query' ),
				documentId: linkToTrack.getAttribute( 'data-document-id' ),
				requestId: linkToTrack.getAttribute( 'data-request-id' ),
			} );
		} );
	} );
};

const handleSearchAutoComplete = ( client, searchField ) => {
	const searchContainer = document.querySelector( '.hero-search__input-container' );
	const searchAutocompleteContainer = document.querySelector( '.st-default-autocomplete' );
	if ( searchAutocompleteContainer ) {
		searchAutocompleteContainer.classList.add( 'd-none' );

		searchField.addEventListener( 'focus', () => {
			searchAutocompleteContainer.classList.remove( 'd-none' );
		} );

		window.addEventListener( 'click', ( event ) => {
			if ( ! searchContainer.contains( event.target ) ) {
				searchAutocompleteContainer.classList.add( 'd-none' );
			}
		} );

		searchField.addEventListener( 'keydown', ( event ) => {
			if ( event.key === 'Enter' ) {
				searchAutocompleteContainer.classList.add( 'd-none' );
				searchField.blur();
			}
		} );

		searchField.addEventListener(
			'keyup',
			debounce( ( event ) => {
				const searchQuery = event.target.value;

				if ( event.target.value === '' ) {
					searchAutocompleteContainer.innerHTML = '';
				} else {
					client
						.search( searchQuery, {
							page: { size: PAGE_SIZE, current: 1 },
						} )
						.then( ( response ) => {
							if ( response.results.length > 0 ) {
								const resultHtml = `
								<div class="st-autocomplete-results st-ui-autocomplete">
									<div class="st-query-present">
									${ response.results
										.map( ( result ) => {
											return `
												<a
													class="st-ui-result __swiftype_result"
													href="${ result.getRaw( 'url' ) }"
													data-document-id="${ result.getRaw( 'id' ) }"
													data-request-id="${ response.info.meta.request_id }"
													data-query="${ searchQuery }"
												>
													<span class="st-ui-type-heading">
															${ highlightTitle( getTitleFromRow( result ), searchQuery ) }
													</span>
												</a>
											`;
										} )
										.join( '' ) }
									</div>
								</div>
								`;
								searchAutocompleteContainer.innerHTML = resultHtml;
							} else {
								searchAutocompleteContainer.innerHTML = '';
							}
						} )
						.catch( () => {
							// Do nothing now - maybe add some error handler later.
						} )
						.finally( () => {
							attachAnalyticsClickTracking( client );
						} );
				}
			}, DEBOUNCE_TIME )
		);
	}
};

const getTitleFromRow = ( row ) => {
	const title = row.getRaw( 'title' );
	if ( title ) {
		return title;
	}
	const headings = row.getRaw( 'headings' );
	if ( headings && headings.length > 0 ) {
		return headings[ 0 ];
	}
	return '';
};

const handleSearchModal = ( client, searchField ) => {
	const renderModal = ( searchQuery, page = 1, focusOnSearchInput = false ) => {
		client
			.search( searchQuery, {
				page: { size: PAGE_SIZE, current: page },
			} )
			.then( ( response ) => {
				const totalPages = response.info.meta.page.total_pages;
				const totalResults = response.info.meta.page.total_results;

				const searchModalContainer = document.createElement( 'div' );
				searchModalContainer.classList.add( 'st-ui-container' );
				searchModalContainer.classList.add( 'st-ui-container-overlay' );
				searchModalContainer.classList.add( 'st-ui-injected-overlay-container' );
				searchModalContainer.innerHTML = `
					<!-- HEADER -->
					<div class="st-ui-container-header_bar st-position-container">
						<section class="st-ui-header">
							<input
								class="st-default-search-input st-search-set-focus"
								type="text"
								value="${ searchField.value }"
								placeholder="Search this site"
								aria-label="Search this site"
								id="searchFieldModal"
								autoComplete="off"
								autoCorrect="off"
								autoCapitalize="off"
							>
							<span class="st-ui-search-icon"></span>
							<a class="st-ui-close-button st-search-hide-outputs">Close</a>
						</section>
					</div>

					<!-- PRIMARY CONTENT -->
					<div class="st-ui-container-primary_content st-position-container">
						<section class="st-ui-content st-search-results">
							<div class="st-query-present">
								${ response.results
									.map( ( result ) => {
										return `
										<a
											href="${ result.getRaw( 'url' ) }"
											class="st-ui-result __swiftype_result"
											data-document-id="${ result.getRaw( 'id' ) }"
											data-request-id="${ response.info.meta.request_id }"
											data-query="${ searchQuery }"
										>
											<span class="st-ui-type-heading">${ highlightTitle(
												getTitleFromRow( result ),
												searchQuery
											) }</span>
											<span class="st-ui-type-detail">
												<span class="st-ui-type-detail-bold">${ highlightTitle(
													result.getRaw( 'meta_description' ),
													searchQuery
												) }
												&nbsp;•&nbsp;
												${ result.getRaw( 'url' ) }
											</span>
										</a>
									`;
									} )
									.join( '' ) }
							</div>
						</section>
					</div>

					<!-- FOOTER -->
					<div class="st-ui-container-footer_bar st-position-container">
						<section class="st-ui-footer">
							<span class="st-ui-search-summary st-search-summary" aria-live="polite">
								<div class="st-query-present">
									${
										response.results.length > 0
											? `Showing <strong>${ page * PAGE_SIZE - 9 }</strong>–<strong>${
													page * PAGE_SIZE - ( PAGE_SIZE - response.results.length )
											  }</strong> of <strong>${ totalResults }</strong>`
											: 'No results found '
									}
									<span class="st-ui-search-summary-query"> for “${ searchQuery }” </span>
								</div>
							</span>

							<span class="st-ui-pagination st-search-pagination">
								<div class="st-query-present">
								${
									page > 1
										? `
										<a class="st-ui-pagination-link st-result-pagination-link" data-page="${ page - 1 }" href="#0">
											<span class="st-ui-arrow left-arrow"></span> previous
										</a>
										<a class="st-ui-pagination-link st-ui-pagination-number-link st-result-pagination-link" data-page="${
											page - 1
										}" href="#0">${ page - 1 }</a>
										`
										: ''
								}
									${
										totalPages > 1
											? `<a class="st-ui-pagination-link st-ui-pagination-number-link st-result-pagination-link active" href="#0">${ page }</a>`
											: ''
									}
									${ [ 1, 2, 3, 4 ]
										.map( ( nextPage ) =>
											page + nextPage <= totalPages
												? `<a class="st-ui-pagination-link st-ui-pagination-number-link st-result-pagination-link" data-page="${
														page + nextPage
												  }" href="#0">${ page + nextPage }</a>`
												: ''
										)
										.join( '' ) }
									${
										page < totalPages
											? `
												<a class="st-ui-pagination-link st-result-pagination-link" data-page="2" href="#0">
													next <span class="st-ui-arrow right-arrow"></span>
												</a>
											`
											: ''
									}
								</div>
							</span>
						</section>
					</div>
					`;

				if ( ! document.querySelector( '.st-ui-overlay' ) ) {
					const searchModalBackdrop = document.createElement( 'div' );
					searchModalBackdrop.classList.add( 'st-ui-overlay' );
					searchModalBackdrop.classList.add( 'st-search-hide-outputs' );
					document.body.appendChild( searchModalBackdrop );
				}
				const existingSearchModalContainer = document.querySelector( '.st-ui-container' );
				if ( existingSearchModalContainer ) {
					existingSearchModalContainer.innerHTML = searchModalContainer.innerHTML;
				} else {
					document.body.appendChild( searchModalContainer );
				}

				Array.from( document.querySelectorAll( '.st-search-hide-outputs' ) ).forEach(
					( closeSearchModalNode ) => {
						closeSearchModalNode.addEventListener( 'click', () => {
							document.querySelector( '.st-ui-overlay' )?.remove();
							document.querySelector( '.st-ui-container' )?.remove();
						} );
					}
				);

				Array.from( document.querySelectorAll( '.st-ui-pagination-link[data-page]' ) ).forEach(
					( paginationLink ) => {
						paginationLink.addEventListener( 'click', ( event ) => {
							event.preventDefault();
							renderModal( searchQuery, parseInt( event.target.dataset.page ) );
						} );
					}
				);

				document.querySelector( '#searchFieldModal' ).addEventListener(
					'keyup',
					debounce( ( event ) => {
						searchField.value = event.target.value;
						renderModal( event.target.value );
					}, DEBOUNCE_TIME )
				);

				if ( focusOnSearchInput ) {
					document.querySelector( '#searchFieldModal' )?.focus(); // @TODO Not working.
				}

				attachAnalyticsClickTracking( client );
			} );
	};

	searchField.addEventListener( 'keydown', ( event ) => {
		if ( event.key === 'Enter' ) {
			renderModal( event.target.value, 1, true );
		}
	} );
};
